import { CheckCircle2, Clock, MonitorSmartphone, SlidersHorizontal, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Preferences } from "../build5/Preferences";
import { loadViewerData, putPreferences } from "../build5/api5";

const DEFAULT_PREFS = { country_codes: [], continent_codes: [], date_from_year: null, date_to_year: null, slideshow_seconds: 10, always_show_details: true };

// Format a raw code into ABCD-1234 (Crockford-ish, uppercase, no dashes stored).
function fmtCode(raw) {
  const c = (raw || "").toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 8);
  return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
}

// Phone/web approval screen (/link). Uses the Supabase-authenticated account API.
// The device token is NEVER returned to or handled here — approval only flips the
// pairing to "approved"; the TV receives the token via its own poll.
export function LinkApproval({ me }) {
  const [code, setCode] = useState("");
  const [phase, setPhase] = useState("input"); // input | looking | confirm | approved | denied | notfound | expired | error
  const [info, setInfo] = useState(null);
  const [deviceName, setDeviceName] = useState("");
  const [busy, setBusy] = useState(false);

  // Persistent TV-controller state (available once paired). Reuses the existing
  // Preferences ("Change where & when") UI — no second filter implementation.
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [countries, setCountries] = useState([]);
  const [continents, setContinents] = useState([]);
  const [isMobile] = useState(() => (typeof window !== "undefined"
    ? window.matchMedia("(max-width: 900px)").matches : true));

  // Load the account's current preferences + geo lists once the TV is linked, so the
  // controller can open the filter UI immediately and repeatedly. Shares the page's single
  // per-account load with WorldExperience (api5.loadViewerData) — no second fetch.
  const uid = me?.www_user_id;
  const loadControllerData = useCallback(async () => {
    const v = await loadViewerData(uid);
    if (v.prefs) setPrefs({ ...DEFAULT_PREFS, ...v.prefs });
    setCountries(v.countries);
    setContinents(v.continents);
  }, [uid]);
  useEffect(() => { if (phase === "approved") loadControllerData(); }, [phase, loadControllerData]);

  // Save filters -> updates shared account preferences (the TV picks them up via its
  // heartbeat/preference-version). Does NOT launch any viewer/slideshow on the phone;
  // it closes the modal and returns to the persistent linked-success/control state.
  const savePrefs = useCallback(async (body) => {
    const r = await putPreferences(body);
    if (!r.ok) { toast.error(r.data?.detail || "Could not save your filters"); return; }
    setPrefs({ ...DEFAULT_PREFS, ...r.data });
    setPrefsOpen(false);
    toast.success("Filters saved — your TV will update automatically.");
  }, []);

  const resetPrefs = useCallback(async () => {
    const body = { ...DEFAULT_PREFS, slideshow_seconds: prefs.slideshow_seconds || 10, always_show_details: prefs.always_show_details };
    const r = await putPreferences(body);
    if (!r.ok) { toast.error(r.data?.detail || "Could not clear your filters"); return; }
    setPrefs({ ...DEFAULT_PREFS, ...r.data });
    toast.success("Filters cleared — showing the whole world.");
  }, [prefs.slideshow_seconds, prefs.always_show_details]);

  const lookup = useCallback(async (rawCode) => {
    const uc = fmtCode(rawCode || code);
    if (uc.replace("-", "").length < 8) { setPhase("input"); return; }
    setPhase("looking");
    const r = await api(`/account/pair/lookup?user_code=${encodeURIComponent(uc)}`);
    if (r.status === 404) { setPhase("notfound"); return; }
    if (r.status === 410) { setPhase("expired"); return; }
    if (!r.ok) { setPhase("error"); return; }
    setInfo(r.data);
    setDeviceName(r.data.name_suggestion || "Living Room TV");
    setPhase("confirm");
  }, [code]);

  // Prefill + auto-lookup when arriving from the QR (?code=…). The URL only ever
  // carries the user-facing pairing code — never a device code or token.
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const c = qs.get("code");
    if (c) { setCode(fmtCode(c)); lookup(c); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => {
    setBusy(true);
    const r = await api("/account/pair/approve", {
      method: "POST",
      body: { user_code: info.user_code, device_name: (deviceName.trim() || "WWW device") },
    });
    setBusy(false);
    if (r.ok) {
      // Successful in-session pairing: consumed the one-time code. Scrub ?code= from the
      // URL so a reload never re-looks-up the now-consumed code and shows "not found".
      try { window.history.replaceState({}, document.title, window.location.pathname); } catch (_e) { /* noop */ }
      setPhase("approved");
    }
    else if (r.status === 410) setPhase("expired");
    else setPhase("error");
  };

  const deny = async () => {
    setBusy(true);
    await api("/account/pair/deny", { method: "POST", body: { user_code: info.user_code } });
    setBusy(false);
    setPhase("denied");
  };

  const reset = () => { setPhase("input"); setInfo(null); setCode(""); };

  return (
    <div className="ww-auth">
      <div className="app">
        <header className="hdr">
          <div className="brand auth-brand">
            <img src="/brand/www-logo-primary.png" className="auth-logo"
                 alt="World Wide Wallpaper" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <span className="tag">Link a device</span>
          </div>
        </header>
        <main className="main">
          <div className="card" data-testid="link-approval" style={{ maxWidth: 460, margin: "0 auto" }}>

            {phase === "input" || phase === "looking" || phase === "notfound" || phase === "error" ? (
              <>
                <h3><MonitorSmartphone size={16} /> Link a device to your account</h3>
                <p className="muted small">Enter the code shown on your TV, or open the link from its QR code.</p>
                <label className="field-label">Pairing code</label>
                <input data-testid="link-code-input" value={code} inputMode="text" autoCapitalize="characters"
                       placeholder="ABCD-1234" maxLength={9}
                       onChange={(e) => setCode(fmtCode(e.target.value))}
                       onKeyDown={(e) => { if (e.key === "Enter") lookup(code); }} />
                {phase === "notfound" && <p className="muted small" data-testid="link-notfound" style={{ color: "#b4462f" }}>We couldn't find that code. Check the TV and try again.</p>}
                {phase === "error" && <p className="muted small" data-testid="link-error" style={{ color: "#b4462f" }}>Something went wrong. Please try again.</p>}
                <button className="btn primary" data-testid="link-lookup" disabled={phase === "looking"}
                        onClick={() => lookup(code)} style={{ marginTop: 12 }}>
                  {phase === "looking" ? "Checking…" : "Continue"}
                </button>
                <p className="muted small" style={{ marginTop: 14 }}>Signed in as <b>{me?.email}</b></p>
              </>
            ) : null}

            {phase === "confirm" && info && (
              <div data-testid="link-confirm">
                <h3><MonitorSmartphone size={16} /> Link this {info.platform || "device"}?</h3>
                <p className="muted small">A device is asking to show your World Wide Wallpaper slideshow. Only approve devices you recognise.</p>
                <label className="field-label">Device name</label>
                <input data-testid="link-device-name" value={deviceName}
                       onChange={(e) => setDeviceName(e.target.value)} placeholder="Living Room TV" maxLength={60} />
                <p className="muted small" style={{ marginTop: 6 }}><Clock size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} /> Code {info.user_code}</p>
                <div className="row wrap" style={{ marginTop: 14 }}>
                  <button className="btn primary" data-testid="link-approve" disabled={busy} onClick={approve}>
                    {busy ? "…" : "Link this device"}
                  </button>
                  <button className="btn ghost" data-testid="link-deny" disabled={busy} onClick={deny}>Not mine</button>
                </div>
              </div>
            )}

            {phase === "approved" && (
              <div data-testid="link-approved" style={{ textAlign: "center", padding: "8px 0" }}>
                <CheckCircle2 size={40} style={{ color: "#2f8f5b" }} />
                <h3 style={{ justifyContent: "center" }}>TV linked successfully</h3>
                <p className="muted">Your slideshow is playing on the TV. Use this phone as its remote — change what it shows whenever you like.</p>
                <div className="row wrap" style={{ marginTop: 16, justifyContent: "center", gap: 10 }}>
                  <button className="btn primary" data-testid="link-change-filters"
                          onClick={() => setPrefsOpen(true)}>
                    <SlidersHorizontal size={16} /> Change filters
                  </button>
                  <button className="btn ghost" data-testid="link-go-www"
                          onClick={() => { window.location.assign("/"); }}>
                    Go to World Wide Wallpaper home
                  </button>
                </div>
                <p className="muted small" style={{ marginTop: 14 }}>Manage or unlink this TV any time under <b>Your devices</b>.</p>
              </div>
            )}

            {phase === "denied" && (
              <div data-testid="link-denied" style={{ textAlign: "center", padding: "8px 0" }}>
                <XCircle size={40} style={{ color: "#b4462f" }} />
                <h3 style={{ justifyContent: "center" }}>Request declined</h3>
                <p className="muted">No device was linked. If this was you, generate a fresh code on the TV.</p>
                <button className="btn" data-testid="link-try-again" onClick={reset}>Enter another code</button>
              </div>
            )}

            {phase === "expired" && (
              <div data-testid="link-expired" style={{ textAlign: "center", padding: "8px 0" }}>
                <Clock size={40} style={{ color: "#8A5518" }} />
                <h3 style={{ justifyContent: "center" }}>That code expired</h3>
                <p className="muted">Pairing codes are short-lived for your security. Generate a new code on the TV and try again.</p>
                <button className="btn" data-testid="link-try-again" onClick={reset}>Enter another code</button>
              </div>
            )}

          </div>
        </main>
      </div>

      <Preferences
        open={prefsOpen}
        isMobile={isMobile}
        onClose={() => setPrefsOpen(false)}
        prefs={prefs}
        countries={countries}
        continents={continents}
        onApply={savePrefs}
        onReset={resetPrefs}
      />
    </div>
  );
}
